import React, { useCallback, useEffect, useRef, useState } from "react";
import "./StoryGame.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownLeftAndUpRightToCenter,
  faClose,
} from "@fortawesome/free-solid-svg-icons";
import {
  ImageMatchGamePages,
  Page,
  Story,
  StoryChoiceId,
  loadStoryById,
} from "./models/story";
import MinimizedStory from "./components/MinimizedStory";
import StorySpreadReader from "./components/StorySpreadReader";
import StoryImageMatchRound from "./components/StoryImageMatchRound";
import { useStoryArrowKeys } from "./hooks/useStoryArrowKeys";
import { useStoryReaderLayout } from "./hooks/useStoryReaderLayout";
import {
  canStepNext,
  canStepPrev,
  getVisiblePages,
  snapCursorForSpread,
  stepNext,
  stepPrev,
} from "./readingLayout";

type Props = {
  onCancel: () => void;
  /** Reopen the story picker (e.g. close reader and show list). Falls back to `onCancel` if omitted. */
  onPickAnotherStory?: () => void;
  storyId: StoryChoiceId;
};

enum Mode {
  STORY = "story",
  IMAGE_MATCH = "image_match",
}

const StoryGame = (props: Props) => {
  const { onCancel, onPickAnotherStory, storyId } = props;
  const layout = useStoryReaderLayout();
  const prevLayoutRef = useRef<typeof layout | null>(null);

  const [gameMode, setGameMode] = useState<Mode>(Mode.STORY);
  const [story, setStory] = useState<Story>();
  const [cursorIndex, setCursorIndex] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [imageMatchPages, setImageMatchPages] = useState<ImageMatchGamePages>();
  const [showImageMatchResults, setShowImageMatchResults] = useState(false);
  const [showPlayAgainButton, setShowPlayAgainButton] = useState(false);
  const [imageMatchClickedPage, setImageMatchClickedPage] = useState<Page>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadStory = async () => {
      setLoading(true);
      setGameMode(Mode.STORY);
      setIsMinimized(false);
      setShowImageMatchResults(false);
      setShowPlayAgainButton(false);
      setImageMatchPages(undefined);
      setImageMatchClickedPage(undefined);
      setCursorIndex(0);

      const newStory = await loadStoryById(storyId);
      if (cancelled) return;

      setStory(newStory);
      setLoading(false);
    };

    loadStory();

    return () => {
      cancelled = true;
    };
  }, [storyId]);

  useEffect(() => {
    const prev = prevLayoutRef.current;
    if (prev === "single" && layout === "spread") {
      setCursorIndex((i) => snapCursorForSpread(i));
    }
    prevLayoutRef.current = layout;
  }, [layout]);

  const pages = story?.readingPages ?? [];
  const visiblePages = story
    ? getVisiblePages(pages, cursorIndex, layout)
    : [];
  const firstVisible = visiblePages[0];
  const lastVisible = visiblePages[visiblePages.length - 1];

  const hasPreviousPage = story
    ? canStepPrev(cursorIndex, layout)
    : false;
  const hasNextPage = story
    ? canStepNext(cursorIndex, layout, pages.length)
    : false;

  const goToPrevPages = useCallback(() => {
    const next = stepPrev(cursorIndex, layout);
    if (next !== null) setCursorIndex(next);
  }, [cursorIndex, layout]);

  const goToNextPages = useCallback(() => {
    const next = stepNext(cursorIndex, layout, pages.length);
    if (next !== null) setCursorIndex(next);
  }, [cursorIndex, layout, pages.length]);

  const goToSpread = useCallback((spreadIndex: number) => {
    setCursorIndex(spreadIndex * 2);
  }, []);

  useStoryArrowKeys({
    enabled:
      !loading &&
      !!story &&
      !isMinimized &&
      gameMode === Mode.STORY,
    story,
    firstVisiblePage: firstVisible,
    lastVisiblePage: lastVisible,
    onPrev: goToPrevPages,
    onNext: goToNextPages,
  });

  const loadImageMatchGame = () => {
    setShowImageMatchResults(false);
    setShowPlayAgainButton(false);
    setImageMatchClickedPage(undefined);
    setImageMatchPages(story?.getPagesForImageMatchGame());
    setGameMode(Mode.IMAGE_MATCH);
  };

  const handleStoryMatchImageClick = (clicked: Page) => {
    setImageMatchClickedPage(clicked);
    setShowImageMatchResults(true);
    setShowPlayAgainButton(true);
  };

  const minimizeStory = () => setIsMinimized(true);

  const backToStories = () => {
    (onPickAnotherStory ?? onCancel)();
  };

  if (loading === true || !story) return null;

  if (visiblePages.length === 0 || !firstVisible || !lastVisible) return null;

  if (isMinimized) {
    return (
      <MinimizedStory
        leftPage={firstVisible}
        rightPage={visiblePages[1] ?? firstVisible}
        onClick={() => setIsMinimized(false)}
      />
    );
  }

  return (
    <div className="StoryGameContainer">
      <header className="StoryGameHeader">
        <button
          type="button"
          className="StoryGameBackLink"
          onClick={backToStories}
        >
          ← Choose a story
        </button>
        <div className="WindowControls">
          <button type="button" className="WindowControl" onClick={minimizeStory}>
            <FontAwesomeIcon icon={faDownLeftAndUpRightToCenter} />
          </button>
          <button type="button" className="WindowControl" onClick={onCancel}>
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>
      </header>
      {gameMode === Mode.STORY && (
        <StorySpreadReader
          story={story}
          visiblePages={visiblePages}
          layout={layout}
          hasPreviousPage={hasPreviousPage}
          hasNextPage={hasNextPage}
          onPrev={goToPrevPages}
          onNext={goToNextPages}
          onFinish={loadImageMatchGame}
          onMinimize={minimizeStory}
          onGoToSpread={goToSpread}
        />
      )}
      {gameMode === Mode.IMAGE_MATCH && imageMatchPages && (
        <StoryImageMatchRound
          imageMatchPages={imageMatchPages}
          showImageMatchResults={showImageMatchResults}
          imageMatchClickedPage={imageMatchClickedPage}
          showPlayAgainButton={showPlayAgainButton}
          onPickImage={handleStoryMatchImageClick}
          onPlayAgain={loadImageMatchGame}
          onMinimize={minimizeStory}
        />
      )}
    </div>
  );
};

export default StoryGame;
